import React, { useState, useEffect } from 'react';
import { functions } from '../../../firebase/config';
import { httpsCallable } from 'firebase/functions';
import { 
  TextField, 
  Button, 
  Modal, 
  Box, 
  Typography, 
  Autocomplete,
  InputAdornment,
  IconButton,
  CircularProgress 
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker, TimePicker } from '@mui/x-date-pickers';
import { FiUsers } from 'react-icons/fi';
import './CreateMeetingModal.css';
import ParticipantSelectionModal from './ParticipantSelectionModal';
import { db } from '../../../firebase/config';
import { useAuthStore } from '../../../auth/state';
import { doc, getDoc } from 'firebase/firestore';

// # Add any icons you want to use here
// import { Event, Group, Description, Close } from '@mui/icons-material';

function CreateMeetingModal({ open, onClose, meetingType, onSuccess }) {
  const { userProfile } = useAuthStore();
  // # Form state management
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    selectedDate: null,
    selectedTime: null,
    selectedUsers: [],
    meetingUrl: process.env.REACT_APP_MENTOR_MEETING_URL || 'https://meet.google.com/default-meeting' // # Default mentor URL
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showParticipantModal, setShowParticipantModal] = useState(false);
  const [loadingMentorUrl, setLoadingMentorUrl] = useState(false);

  // # Fetch mentor's meeting URL when modal opens
  useEffect(() => {
    const fetchMentorUrl = async () => {
      if (!open || !userProfile?.uid) return;
      
      setLoadingMentorUrl(true);
      try {
        // # First try to get URL from mentors collection
        const mentorDoc = await getDoc(doc(db, 'mentors', userProfile.uid));
        
        if (mentorDoc.exists()) {
          const mentorData = mentorDoc.data();
          const personalZoomUrl = mentorData.personal?.zoom_url;
          
          if (personalZoomUrl) {
            setFormData(prev => ({
              ...prev,
              meetingUrl: personalZoomUrl
            }));
          }
        }
      } catch (error) {
        console.error('Error fetching mentor URL:', error);
        // # Don't set error - fallback to default URL
      } finally {
        setLoadingMentorUrl(false);
      }
    };

    fetchMentorUrl();
  }, [open, userProfile?.uid]);

  // # Handle form data changes
  const handleFormChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
    setError('');
  };

  // # Handle date/time changes
  const handleDateChange = (newDate) => {
    setFormData({
      ...formData,
      selectedDate: newDate
    });
    setError('');
  };

  const handleTimeChange = (newTime) => {
    setFormData({
      ...formData,
      selectedTime: newTime
    });
    setError('');
  };

  // # Reset form helper
  const resetForm = () => {
    setFormData(prev => ({
      title: '',
      description: '',
      selectedDate: null,
      selectedTime: null,
      selectedUsers: [],
      meetingUrl: prev.meetingUrl // # Keep the fetched URL
    }));
    setError('');
  };

  // # Combine date and time for meeting
  const combineDateAndTime = () => {
    if (!formData.selectedDate || !formData.selectedTime) return null;
    const date = new Date(formData.selectedDate);
    const time = new Date(formData.selectedTime);
    
    return new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      time.getHours(),
      time.getMinutes()
    );
  };

  // # Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // # Validate form
      const { title, description, selectedDate, selectedTime, selectedUsers, meetingUrl } = formData;
      
      if (!title?.trim()) throw new Error('Please enter a meeting title');
      if (!description?.trim()) throw new Error('Please enter a meeting description');
      if (!selectedDate) throw new Error('Please select a meeting date');
      if (!selectedTime) throw new Error('Please select a meeting time');
      if (!selectedUsers?.length) throw new Error('Please select at least one participant');
      if (!meetingUrl?.trim()) throw new Error('Meeting URL is required');

      const meetTime = combineDateAndTime();
      if (!meetTime) throw new Error('Invalid date/time combination');

      // # Get callable reference to cloud function
      const createMeetingFn = httpsCallable(functions, 'createMeeting');
      
      // # Call the function with meeting data
      const result = await createMeetingFn({
        type: meetingType,
        title: title.trim(),
        description: description.trim(),
        meetTime: meetTime.toISOString(),
        invited: selectedUsers.map(user => user.uid),
        meetingUrl: meetingUrl.trim(),
        hostData: {
          uid: userProfile.uid,
          displayName: `${userProfile.firstName || ''} ${userProfile.lastName || ''}`.trim(),
          email: userProfile.email
        }
      });

      // # Check result
      if (result.data && result.data.success) {
        console.log(`Meeting created with ID: ${result.data.meetingId}`);
        resetForm();
        onSuccess && onSuccess(result.data.meetingId);
      } else {
        throw new Error(result.data?.message || 'Failed to create meeting: Unknown error');
      }
    } catch (err) {
      console.error('Error creating meeting:', err);
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        onClose();
        resetForm();
      }}
      className="create_meeting_modal"
    >
      <Box className="create_meeting_modal_content">
        <Typography variant="h4" className="create_meeting_modal_title">
          Schedule New Meeting
        </Typography>

        <form onSubmit={handleSubmit} className="create_meeting_form">
          {/* # Title Input */}
          <TextField
            label="Meeting Title"
            value={formData.title}
            onChange={handleFormChange('title')}
            fullWidth
            required
            margin="normal"
            error={!!error && !formData.title.trim()}
            helperText={!!error && !formData.title.trim() ? 'Title is required' : ''}
            placeholder="Enter a descriptive title for your meeting"
          />

          {/* # Meeting URL Input */}
          <div className="create_meeting_url_section">
            <TextField
              label="Meeting URL"
              value={formData.meetingUrl}
              onChange={handleFormChange('meetingUrl')}
              fullWidth
              required
              margin="normal"
              className="create_meeting_url_input"
              error={!!error && !formData.meetingUrl.trim()}
              helperText={!!error && !formData.meetingUrl.trim() ? 'Meeting URL is required' : ''}
              placeholder="Enter meeting URL"
              InputProps={{
                className: 'create_meeting_url_field',
                startAdornment: (
                  <InputAdornment position="start">
                    <span className="create_meeting_url_icon">🔗</span>
                  </InputAdornment>
                ),
                endAdornment: loadingMentorUrl && (
                  <InputAdornment position="end">
                    <CircularProgress size={20} />
                  </InputAdornment>
                )
              }}
            />
          </div>

          {/* # Date and Time Selection */}
          <div className="create_meeting_datetime_section">
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Meeting Date"
                value={formData.selectedDate}
                onChange={handleDateChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    error={!!error && !formData.selectedDate}
                    helperText={!!error && !formData.selectedDate ? 'Date is required' : ''}
                    className="create_meeting_date_input"
                  />
                )}
              />
              <TimePicker
                label="Meeting Time"
                value={formData.selectedTime}
                onChange={handleTimeChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    error={!!error && !formData.selectedTime}
                    helperText={!!error && !formData.selectedTime ? 'Time is required' : ''}
                    className="create_meeting_time_input"
                  />
                )}
              />
            </LocalizationProvider>
          </div>

          {/* # Participants Selection */}
          <div className="create_meeting_participants_section">
            <Button
              fullWidth
              variant="outlined"
              onClick={() => setShowParticipantModal(true)}
              className="create_meeting_participants_btn"
              startIcon={<FiUsers className="participant_icon" />}
            >
              {formData.selectedUsers.length > 0 
                ? `${formData.selectedUsers.length} Participant${formData.selectedUsers.length > 1 ? 's' : ''} Selected`
                : 'Select Participants'}
            </Button>
            {/* # Show selected participants names for better UX */}
            {formData.selectedUsers.length > 0 && (
              <div className="create_meeting_selected_users">
                {formData.selectedUsers.map(user => (
                  <div key={user.uid} className="create_meeting_selected_user">
                    {user.displayName || user.email || `User ${user.uid.substring(0, 6)}`}
                  </div>
                ))}
              </div>
            )}
            {!!error && formData.selectedUsers.length === 0 && (
              <Typography color="error" className="create_meeting_error">
                Please select at least one participant
              </Typography>
            )}
          </div>

          {/* # Participant Selection Modal */}
          <ParticipantSelectionModal
            open={showParticipantModal}
            onClose={() => setShowParticipantModal(false)}
            selectedUsers={formData.selectedUsers}
            onUpdateSelection={(newSelection) => {
              console.log('Selected users:', newSelection.map(u => ({uid: u.uid, name: u.displayName || u.email})));
              setFormData(prev => ({
                ...prev,
                selectedUsers: newSelection
              }));
              setError('');
            }}
          />

          {/* # Description Input */}
          <TextField
            label="Meeting Description"
            value={formData.description}
            onChange={handleFormChange('description')}
            fullWidth
            required
            margin="normal"
            multiline
            rows={2}
            className="create_meeting_description"
            error={!!error && !formData.description.trim()}
            helperText={!!error && !formData.description.trim() ? 'Description is required' : ''}
            placeholder="Describe the purpose and agenda of the meeting"
          />

          {/* # Error Message */}
          {error && (
            <Typography color="error" className="create_meeting_error">
              {error}
            </Typography>
          )}

          {/* # Action Buttons */}
          <div className="create_meeting_actions">
            <Button
              type="button"
              onClick={() => {
                onClose();
                resetForm();
              }}
              disabled={loading}
              className="create_meeting_cancel_btn"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
              className="create_meeting_submit_btn"
            >
              {loading ? 'Scheduling...' : 'Schedule Meeting'}
            </Button>
          </div>
        </form>
      </Box>
    </Modal>
  );
}

export default CreateMeetingModal; 